import type { AddDraftBranchStepRequest } from '@bliks-io/contracts';
import type { EdgeDescriptor } from '@bliks-io/ui';
import { resolveCurrentLaneKey } from '../graph/resolveLane';
import { transformDraftNode } from '@/api/draftGraph';
import { addSourceToNodeInputs } from '../../../../../api/mutations/helpers/draftGraphOperations';
import {
  appendPlaceholderPosition,
  buildPlaceholderStep,
  envelopePlaceholderReconcilers
} from '../../../../../api/mutations/helpers/placeholderStep';
import { mintTempDraftStepId } from '../../../../../api/mutations/useProcessDraft/shared';
import { addDraftBranchStepApi } from '../../../../../api/persist/draft';
import { resolvePosition } from './shared';
import type { EdgeActionDeps } from './types';

async function mergeIntoStep(
  targetStepId: string,
  position: { x: number; y: number },
  deps: EdgeActionDeps
): Promise<void> {
  const { processId } = deps;
  // The merge target can be a step OR a draft assignment (suggestion
  // placeholder) — both live in the shared stableStepId space.
  const targetEntry = deps.graph.findNode(targetStepId);
  // New source-side step joins the target's lane (that's where the merge lands).
  const laneHint =
    targetEntry?.kind === 'step'
      ? resolveCurrentLaneKey(targetEntry.node, deps.getCachedSteps(), deps.getAssigneeLookup?.())
      : undefined;

  const tempId = mintTempDraftStepId();
  const reconcilers = envelopePlaceholderReconcilers(tempId);
  const body: AddDraftBranchStepRequest = {
    anchor: { kind: 'before', targetStableStepId: targetStepId },
    position,
    ...(laneHint ? { laneKey: laneHint } : {})
  };

  await deps.runTransaction({
    optimistic: (prev) => {
      const placeholder = buildPlaceholderStep({
        tempId,
        processId,
        draftId: prev.draft._id,
        laneKey: laneHint
      });
      const rewired = transformDraftNode(prev, targetStepId, (entry) =>
        entry.kind === 'event' ? entry.node : addSourceToNodeInputs(entry.node, tempId)
      );
      return {
        ...rewired,
        draft: { ...rewired.draft, updatedAt: new Date() },
        steps: [...rewired.steps, placeholder]
      };
    },
    optimisticLayout: (prev) => appendPlaceholderPosition(prev, tempId, position),
    persist: () => addDraftBranchStepApi(processId, body),
    reconcile: reconcilers.reconcile,
    reconcileLayout: reconcilers.reconcileLayout
  });
}

export async function performMergeIntoTarget(
  edge: EdgeDescriptor,
  deps: EdgeActionDeps
): Promise<void> {
  const { target } = edge;

  if (target.stepId) {
    await mergeIntoStep(target.stepId, resolvePosition(edge, deps), deps);
  }
}
